import React from 'react'

export function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-sm rounded-lg p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">Privacy Policy for Resistor</h1>

        <div className="text-sm text-gray-600 mb-8">
          <p><strong>Last Updated:</strong> January 15, 2026</p>
          <p><strong>Effective Date:</strong> January 15, 2026</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Overview</h2>
            <p className="text-gray-700 leading-relaxed">
              Resistor is a Chrome extension designed to help you build healthier digital habits through intentional friction.
              This privacy policy explains what data we collect, how we use it, and your rights regarding your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Developer Information</h2>
            <ul className="text-gray-700 space-y-2">
              <li><strong>Extension Name:</strong> Resistor</li>
              <li><strong>Developer:</strong> Antriksh Jain</li>
              <li><strong>Contact Email:</strong> antriksh1980@gmail.com</li>
              <li><strong>Website:</strong> <a href="https://resistor-dashboard.vercel.app" className="text-blue-600 hover:underline">https://resistor-dashboard.vercel.app</a></li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">1. Account Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Email address:</strong> Collected via Google OAuth for authentication</li>
              <li><strong>Google user ID:</strong> Used to identify your account securely</li>
              <li><strong>Display name:</strong> From your Google profile</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2. Browsing Data</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Restricted website domains:</strong> Only the websites you explicitly choose to add resistance to (e.g., instagram.com, facebook.com)</li>
              <li><strong>Access reasons:</strong> The text you enter when accessing restricted sites</li>
              <li><strong>Session timestamps:</strong> When you access restricted sites and for how long</li>
              <li><strong>Session duration:</strong> How long each browsing session lasts</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3. Configuration Data</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Resistance settings:</strong> Time limits and daily limits you set for each website</li>
              <li><strong>Extension preferences:</strong> Your settings and configurations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What We DO NOT Collect</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>❌ We do NOT track your general browsing history</li>
              <li>❌ We do NOT collect data from websites you haven't restricted</li>
              <li>❌ We do NOT sell your data to third parties</li>
              <li>❌ We do NOT use your data for advertising</li>
              <li>❌ We do NOT track you across other websites</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Data</h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Primary Purposes:</h3>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li><strong>Provide the Service:</strong> Display your resistances and enforce your chosen restrictions</li>
              <li><strong>Authentication:</strong> Verify your identity securely using Google OAuth</li>
              <li><strong>Analytics:</strong> Show you your own usage patterns and session history</li>
              <li><strong>Sync:</strong> Keep your settings synchronized across your Chrome browsers</li>
            </ol>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">We Do NOT:</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Share your data with advertisers</li>
              <li>Sell your data to third parties</li>
              <li>Use your data for purposes other than providing the Resistor service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Storage and Security</h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Where Your Data is Stored:</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Database:</strong> Supabase (PostgreSQL) - secure, encrypted database</li>
              <li><strong>Location:</strong> Data is stored in Supabase's secure cloud infrastructure</li>
              <li><strong>Encryption:</strong> All data is encrypted in transit (HTTPS) and at rest</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Security Measures:</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Industry-standard encryption (TLS/SSL)</li>
              <li>Secure authentication via Google OAuth 2.0</li>
              <li>Row-level security policies ensuring you can only access your own data</li>
              <li>Regular security updates and monitoring</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>

            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">1. Google OAuth</h3>
                <p><strong>Purpose:</strong> Secure user authentication</p>
                <p><strong>Data Shared:</strong> Email address, Google user ID, display name</p>
                <p><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://policies.google.com/privacy</a></p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">2. Supabase</h3>
                <p><strong>Purpose:</strong> Secure data storage and user management</p>
                <p><strong>Data Shared:</strong> All user data mentioned above</p>
                <p><strong>Privacy Policy:</strong> <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://supabase.com/privacy</a></p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">3. Vercel</h3>
                <p><strong>Purpose:</strong> Hosting the web dashboard and API</p>
                <p><strong>Data Shared:</strong> API requests and responses</p>
                <p><strong>Privacy Policy:</strong> <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://vercel.com/legal/privacy-policy</a></p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights and Controls</h2>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Access Your Data</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>View all your data anytime in the dashboard</li>
              <li>See all resistances, sessions, and reasons you've entered</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Modify Your Data</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Edit or delete resistances anytime</li>
              <li>Update your time limits and settings</li>
              <li>Change which websites you restrict</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Delete Your Data</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>You can delete your account and all associated data at any time</li>
              <li>Contact us at antriksh1980@gmail.com to request account deletion</li>
              <li>Upon deletion, all your data will be permanently removed within 30 days</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Export Your Data</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Contact us to request a copy of your data</li>
              <li>We will provide your data in a machine-readable format (JSON)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>Active accounts:</strong> Data is retained as long as your account is active</li>
              <li><strong>Deleted accounts:</strong> Data is permanently deleted within 30 days of account deletion</li>
              <li><strong>Session data:</strong> Retained indefinitely unless you delete your account</li>
              <li><strong>Backup retention:</strong> Deleted data may persist in backups for up to 90 days before permanent deletion</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Resistor is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13.
              If you believe a child under 13 has provided us with personal information, please contact us at antriksh1980@gmail.com.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions, concerns, or requests regarding your privacy:
            </p>
            <ul className="text-gray-700 space-y-2">
              <li><strong>Email:</strong> antriksh1980@gmail.com</li>
              <li><strong>Dashboard:</strong> <a href="https://resistor-dashboard.vercel.app" className="text-blue-600 hover:underline">https://resistor-dashboard.vercel.app</a></li>
              <li><strong>Response Time:</strong> We aim to respond within 48 hours</li>
            </ul>
          </section>

          <section className="mb-8 bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Summary (Quick Version)</h2>
            <ul className="text-gray-700 space-y-2">
              <li><strong>What we collect:</strong> Email, websites you restrict, reasons you enter, session times</li>
              <li><strong>Why we collect it:</strong> To provide the Resistor service</li>
              <li><strong>Who we share with:</strong> Nobody (except secure hosting providers: Supabase, Vercel)</li>
              <li><strong>Your control:</strong> You can view, modify, or delete all your data anytime</li>
              <li><strong>Our promise:</strong> We never sell your data or use it for advertising</li>
            </ul>
          </section>

          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-600">
              <strong>Questions?</strong> Email us at antriksh1980@gmail.com
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Last Updated: January 15, 2026
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
